/** Compact time remaining until a poll closes, or `null` once it has ended.
 *
 * Pure by construction: the caller passes `nowMs` so the countdown is
 * deterministic in tests (no ambient clock read here). The component reads
 * the real clock at render and hands it in. Returns the single largest unit
 * that still has whole quantity left — days, then hours, then minutes — which
 * the caller maps to a localized "N days/hours/minutes left" string. */
export type PollTimeLeft = { unit: 'day' | 'hour' | 'minute'; count: number };

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function pollTimeLeft(closesAtIso: string, nowMs: number): PollTimeLeft | null {
  const closesMs = Date.parse(closesAtIso);
  if (Number.isNaN(closesMs)) return null;
  const remaining = closesMs - nowMs;
  if (remaining <= 0) return null;
  // Days ROUND to nearest so a freshly-created 7-day poll reads "7 days left"
  // (it's a hair under 7 days by render time) rather than "6". Hours and
  // minutes FLOOR — rounding them could read "24 hours"/"60 minutes", and
  // near the deadline an honest floor is what a reader wants.
  if (remaining >= DAY) return { unit: 'day', count: Math.round(remaining / DAY) };
  if (remaining >= HOUR) return { unit: 'hour', count: Math.floor(remaining / HOUR) };
  // Under an hour: never show "0 minutes left" while the poll is still open.
  return { unit: 'minute', count: Math.max(1, Math.floor(remaining / MINUTE)) };
}
